import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

export interface IPMS247BookingWidgetProps {
  /**
   * Container ID for the booking widget
   * @default "bb_resBookingBox"
   */
  containerId?: string;

  /**
   * Widget configuration options
   */
  config?: {
    headerColor?: string;
    bodyColor?: string;
    showborder?: boolean;
    BorderColor?: string;
    BorderWidth?: number;
    BorderType?: string;
    FontFamily?: string;
    BodyLanguage?: string;
    FontSize?: string;
    TextColor?: string;
    InputBorderColor?: string;
    InputbackColor?: string;
    InputTextColor?: string;
    ButnBackColor?: string;
    ButnBorderColor?: string;
    ButnTextColor?: string;
    HeaderTextColor?: string;
    HeaderFontSize?: string;
    ShowHeader?: string;
    boxwidth?: string;
    boxwidthtype?: string;
    ShowInlineCSS?: string;
    type?: string;
    acr?: boolean;
    ShowChild?: boolean;
    rooms?: boolean;
    promotion?: boolean;
    defaultadult?: number;
    defaultchild?: number;
    defaultroom?: number;
    ShowNights?: boolean;
    Nonights?: number;
    HTextCaption?: string;
    BtnTextCaption?: string;
    LblPromoCaption?: string;
    LblChkOutCaption?: string;
    Calinit?: boolean;
    CalShowOn?: string;
    CalDefaultDt?: string;
    CalChangeMonth?: boolean;
    CalMinDate?: string;
    CalMaxDate?: string;
    CalDtFormat?: string;
    CalCutoffDays?: string;
    CalImage?: string;
    CalBackColor?: string;
    CalHeaderColor?: string;
    CalCellActiveColor?: string;
    CalCellInActiveColor?: string;
    LblArrivalCaption?: string;
    LblNightsCaption?: string;
    LblAdultsCaption?: string;
    LblChildsCaption?: string;
    LblRoomsCaption?: string;
    LblPerRoomCaption?: string;
    HotelId?: string;
  };

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Form action URL
   * @default "https://live.ipms247.com/booking/book-rooms-beatenbergresortspa"
   */
  formAction?: string;

  /**
   * Form target
   * @default "_blank"
   */
  formTarget?: string;
}

/**
 * IPMS247 Booking Widget Component
 *
 * A reusable React component that integrates the IPMS247 booking widget.
 * This component handles loading external scripts and styles, and initializes
 * the jQuery-based booking widget.
 *
 * @example
 * ```tsx
 * <IPMS247BookingWidget
 *   className="my-8"
 *   config={{
 *     headerColor: "#75dbe0",
 *     bodyColor: "#ddf0ef",
 *     boxwidth: "800"
 *   }}
 * />
 * ```
 */
export const IPMS247BookingWidget: React.FC<IPMS247BookingWidgetProps> = ({
  containerId = "bb_resBookingBox",
  config = {},
  className,
  formAction = "https://live.ipms247.com/booking/book-rooms-beatenbergresortspa",
  formTarget = "_blank",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptsLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);

  // Default configuration matching the provided code
  const defaultConfig = {
    btnContainer: containerId,
    headerColor: "#75dbe0",
    bodyColor: "#ddf0ef",
    showborder: true,
    BorderColor: "#2cdede",
    BorderWidth: 1,
    BorderType: "solid",
    FontFamily: "Arial, Helvetica, sans-serif",
    BodyLanguage: "en",
    FontSize: "12",
    TextColor: "#0a635f",
    InputBorderColor: "#87deda",
    InputbackColor: "#FFFFFF",
    InputTextColor: "#17a690",
    ButnBackColor: "#FFFFFF",
    ButnBorderColor: "#FFFFFF",
    ButnTextColor: "#000000",
    HeaderTextColor: "#000000",
    HeaderFontSize: "1",
    ShowHeader: "1",
    boxwidth: "800",
    boxwidthtype: "TYPE_PX",
    ShowInlineCSS: "1",
    type: "vtype",
    acr: false,
    ShowChild: false,
    rooms: false,
    promotion: true,
    defaultadult: 10,
    defaultchild: 10,
    defaultroom: 16,
    ShowNights: false,
    Nonights: 15,
    HTextCaption: "Reservation",
    BtnTextCaption: "Book Now",
    LblPromoCaption: "",
    LblChkOutCaption: "Check Out",
    Calinit: true,
    CalShowOn: "both",
    CalDefaultDt: "+0w",
    CalChangeMonth: true,
    CalMinDate: "0",
    CalMaxDate: "",
    CalDtFormat: "yy-mm-dd",
    CalCutoffDays: "1",
    CalImage: "1px -346px",
    CalBackColor: "#a0f2ea",
    CalHeaderColor: "#d5f2f1",
    CalCellActiveColor: "#101414",
    CalCellInActiveColor: "#c8dbda",
    LblArrivalCaption: "Arrival",
    LblNightsCaption: "Nights",
    LblAdultsCaption: "Adult",
    LblChildsCaption: "Child",
    LblRoomsCaption: "Rooms",
    LblPerRoomCaption: "Per Room",
    HotelId: "beatenbergresortspa",
  };

  const finalConfig = { ...defaultConfig, ...config };

  useEffect(() => {
    // Load CSS files
    const loadStylesheet = (href: string, id: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if stylesheet is already loaded
        if (document.getElementById(id)) {
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = href;
        link.id = id;
        link.onload = () => resolve();
        link.onerror = () =>
          reject(new Error(`Failed to load stylesheet: ${href}`));
        document.head.appendChild(link);
      });
    };

    // Load JavaScript files
    const loadScript = (
      src: string,
      id: string,
      async: boolean = true,
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if script is already loaded
        const existingScript = document.getElementById(id);
        if (existingScript) {
          // If jQuery, verify it's actually available (with polling)
          if (id === "ipms247-jquery") {
            // First check if jQuery is already available
            if (typeof window !== "undefined" && (window as any).jQuery) {
              resolve();
              return;
            }

            // If script exists but jQuery isn't available yet, wait for it
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max wait
            const checkJQuery = () => {
              attempts++;
              if (typeof window !== "undefined" && (window as any).jQuery) {
                resolve();
              } else if (attempts < maxAttempts) {
                setTimeout(checkJQuery, 100);
              } else {
                // If script exists but jQuery still isn't available after waiting,
                // the script might have failed to load - try reloading
                console.warn(
                  "jQuery script exists but jQuery not available after waiting, attempting to reload...",
                );
                existingScript.remove();
                // Fall through to create new script
              }
            };
            checkJQuery();
            return;
          } else {
            resolve();
            return;
          }
        }

        const script = document.createElement("script");
        script.src = src;
        script.type = "text/javascript";
        script.id = id;

        // For jQuery, we need it to load synchronously (not async) so other scripts can use it
        if (id === "ipms247-jquery") {
          script.async = false;
          script.defer = false;
        } else {
          script.async = async;
        }

        script.onload = () => {
          // For jQuery, wait a bit to ensure it's fully initialized
          if (id === "ipms247-jquery") {
            // Poll for jQuery availability
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max wait
            const checkJQuery = () => {
              attempts++;
              if (typeof window !== "undefined" && (window as any).jQuery) {
                console.log("jQuery loaded successfully");
                resolve();
              } else if (attempts < maxAttempts) {
                setTimeout(checkJQuery, 100);
              } else {
                console.error(
                  "jQuery failed to initialize after loading. Script src:",
                  src,
                );
                reject(new Error("jQuery failed to initialize after loading"));
              }
            };
            // Start checking immediately
            checkJQuery();
          } else {
            resolve();
          }
        };
        script.onerror = (error) => {
          console.error(`Failed to load script: ${src}`, error);
          reject(new Error(`Failed to load script: ${src}`));
        };

        // Insert script - for jQuery, insert before other scripts
        if (id === "ipms247-jquery") {
          // Try to insert before any existing scripts
          const firstScript = document.head.querySelector("script");
          if (firstScript) {
            document.head.insertBefore(script, firstScript);
          } else {
            document.head.appendChild(script);
          }
        } else {
          document.head.appendChild(script);
        }
      });
    };

    // Load all resources
    const loadResources = async () => {
      if (scriptsLoadedRef.current) {
        return;
      }

      try {
        // Load CSS files
        await loadStylesheet(
          "https://live.ipms247.com/themes/reservation/css/bookingbtn/resui_bookingbox.css",
          "ipms247-bookingbox-css",
        );
        await loadStylesheet(
          "https://live.ipms247.com/themes/reservation/css/bookingbtn/resui_datepicker.css",
          "ipms247-datepicker-css",
        );

        // Load JavaScript files in order - jQuery must load synchronously first
        // jQuery must NOT be async so other scripts can depend on it
        await loadScript(
          "https://live.ipms247.com/themes/reservation/js/bookingbtn/jquery-a.l.d.js",
          "ipms247-jquery",
          false, // Not async - must load before other scripts
        );

        // Verify jQuery is available before proceeding (with retry)
        let jqueryReady = false;
        for (let i = 0; i < 10; i++) {
          if (typeof window !== "undefined" && (window as any).jQuery) {
            jqueryReady = true;
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (!jqueryReady) {
          throw new Error("jQuery failed to load or initialize");
        }

        // Now load dependent scripts (these can be async)
        await loadScript(
          "https://live.ipms247.com/themes/reservation/js/bookingbtn/jquery-ui.js",
          "ipms247-jquery-ui",
          true,
        );
        await loadScript(
          "https://live.ipms247.com/themes/reservation/js/bookingbtn/common.js",
          "ipms247-common",
          true,
        );
        await loadScript(
          "https://live.ipms247.com/themes/reservation/js/bookingbtn/resui_resbookingbox.js",
          "ipms247-bookingbox-js",
          true,
        );

        scriptsLoadedRef.current = true;
      } catch (error) {
        console.error("Error loading IPMS247 booking widget resources:", error);
      }
    };

    // Initialize widget after scripts are loaded
    const initializeWidget = () => {
      if (widgetInitializedRef.current || !containerRef.current) {
        return;
      }

      // Wait for jQuery to be available
      const checkJQuery = () => {
        if (typeof window !== "undefined" && (window as any).jQuery) {
          const $ = (window as any).jQuery;

          // Check if the plugin is available
          if ($.fn && $.fn.bb_resBookingBox) {
            try {
              // Initialize the widget
              $(`#${containerId}`).bb_resBookingBox(finalConfig);
              widgetInitializedRef.current = true;
            } catch (error) {
              console.error(
                "Error initializing IPMS247 booking widget:",
                error,
              );
            }
          } else {
            // Retry after a short delay
            setTimeout(checkJQuery, 100);
          }
        } else {
          // Retry after a short delay
          setTimeout(checkJQuery, 100);
        }
      };

      // Start checking for jQuery
      checkJQuery();
    };

    // Load resources and initialize
    loadResources().then(() => {
      // Wait a bit for scripts to fully initialize
      setTimeout(initializeWidget, 500);
    });

    // Cleanup function
    return () => {
      // Note: We don't remove the scripts/styles as they might be used by other instances
      // The widget will be destroyed when the container is removed
    };
  }, [containerId, finalConfig]);
  return (
    <div className={cn("ipms247-booking-widget", className)} data-oid="4ow6x-t">
      <form
        name="_resBBBox"
        action={formAction}
        target={formTarget}
        method="post"
        data-oid="9i2ao72"
      >
        <div
          ref={containerRef}
          className="bb_resbox"
          id={containerId}
          data-oid="3bk5xzc"
        />
      </form>
    </div>
  );
};

export default IPMS247BookingWidget;
